import {
    BackgroundCheckHistoryColumns,
    BackgroundCheckHistoryData,
    backgroundCheckHistoryEntityName,
} from '@/backend/features/background-check';
import { renderHistoryContent } from '@/ui/features/background-check/components/background-check-user-account-section/components/BackgroundCheckHistoryEntityContent';
import { getBackgroundCheckStatusIcon } from '@/ui/features/background-check/components/background-check-user-account-section/utils/getBackgroundCheckStatusIcon';
import { showCustomDialog } from '@juvare/common-web-ui/DialogHelper';
import { themeColors } from '@juvare/common-web-ui/JuvareTheme';
import { useStorageClient } from '@juvare/platform-sdk-react/ui/hooks';
import { ListResponse, Row } from '@juvare/platform-shared/features/storage';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TaskOutlinedIcon from '@mui/icons-material/TaskOutlined';
import { Box, Button, CircularProgress, Grid, Paper, Typography } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';

export type BackgroundCheckHistoryTableProps = {
    backgroundCheckHistoryPages: ListResponse<BackgroundCheckHistoryColumns>[];
    onShowMore: () => void;
    isValidating: boolean;
    isLastPage: boolean;
};

export const BackgroundCheckHistoryTable: React.FC<BackgroundCheckHistoryTableProps> = ({
    backgroundCheckHistoryPages,
    onShowMore,
    isValidating,
    isLastPage,
}) => {
    const { t } = useTranslation('rms');

    const backgroundCheckHistoryClient = useStorageClient<BackgroundCheckHistoryData, BackgroundCheckHistoryColumns>(
        backgroundCheckHistoryEntityName,
    );

    const handleViewReportModal = (data: Row<BackgroundCheckHistoryColumns>) => {
        backgroundCheckHistoryClient.get(data.id).then((response) => {
            showCustomDialog({
                fullScreen: true,
                content: (close, ariaTitleId, ariaContentId) => renderHistoryContent(close, response, t, ariaTitleId, ariaContentId),
            });
        });
    };

    return (
        <Grid container gap={1} pt={0}>
            {backgroundCheckHistoryPages.map(({ rows }) => {
                return rows.map((row) => {
                    const { id, columns } = row;

                    return (
                        <Paper key={id} component={Grid} elevation={0} item xs={12} sx={{ backgroundColor: themeColors.surface.primary }}>
                            <Grid container p={2}>
                                <Grid item alignContent='center' xs={3}>
                                    <Typography variant='subtitle2'>
                                        {t(`backgroundCheck.types.${columns.type}`, {
                                            defaultValue: columns.type,
                                        })}
                                    </Typography>
                                    <Typography variant='overline'>
                                        {t('date', {
                                            value: columns.createdAt,
                                        })}
                                    </Typography>
                                </Grid>
                                <Grid item alignContent='center' xs={3}>
                                    {columns.nextStatus && (
                                        <Box display='inline-flex' alignItems='center'>
                                            {getBackgroundCheckStatusIcon(columns.nextStatus)}{' '}
                                            <Typography variant='body2' pl={1}>
                                                {t(`backgroundCheck.statuses.${columns.nextStatus}`, {
                                                    defaultValue: 'Unknown',
                                                })}
                                            </Typography>
                                        </Box>
                                    )}
                                </Grid>
                                <Grid item alignContent='center' lg={4} sm={1}>
                                    <Typography variant='body2' pl={1}>
                                        {columns.message || columns.note || ''}
                                    </Typography>
                                </Grid>
                                <Grid container item alignContent='center' justifyContent='flex-end' spacing={1} lg={2} sm={5}>
                                    <Grid item display='flex' alignItems='center'>
                                        {columns.withDocument && <TaskOutlinedIcon sx={{ color: themeColors.icon.link }} />}
                                    </Grid>
                                    <Grid item>
                                        <Button
                                            size='small'
                                            data-test='view-report-button'
                                            variant='outlined'
                                            onClick={() => handleViewReportModal(row)}
                                        >
                                            {t('backgroundCheck.labels.viewReport')}
                                        </Button>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Paper>
                    );
                });
            })}
            {!isLastPage && (
                <Grid container justifyContent='center'>
                    <Button
                        size='small'
                        startIcon={isValidating ? <CircularProgress size='20px' /> : <ExpandMoreIcon />}
                        data-test='show-more-button'
                        onClick={onShowMore}
                    >
                        {t('backgroundCheck.labels.showMore')}
                    </Button>
                </Grid>
            )}
        </Grid>
    );
};
